import {Country, User} from '@hconnect/apiclient'
import {useFeaturesCheckFetcher} from '@hconnect/common/components/FeaturesCheck'
import {Content, ResponsiveTable, useBreakPoints} from '@hconnect/uikit'
import {TableFilterItemProps} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/Item/TableFilterItem.types'
import {TableFilterTypeProps} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/TableFilter.enum'
import {TableFilterType} from '@hconnect/uikit/src/lib/Molecules/Table/Filter/TableFilter.types'
import {PageHeader} from '@hm/fundamental'
import {Add, Close, Mail} from '@mui/icons-material'
import {Box, Button, ClickAwayListener, Grid, IconButton, Paper} from '@mui/material'
import React, {CSSProperties, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'

import {api} from '../../App.store'
import {useAdminPilotUser} from '../../common/hooks/useAdminPilotUser'
import {useCountriesConfiguration} from '../../common/hooks/useCountriesConfiguration'
import {useDisableCrossCountryFilter} from '../../common/hooks/useDisableCrossCountryFilter'
import {useRolesConfigurationForUserId} from '../../common/hooks/useRolesConfiguration'
import {useUserCrossCountriesConfiguration} from '../../common/hooks/useUserCrossCountriesConfiguration'
import {useUserRoles} from '../../common/hooks/useUserRoles'
import {useQueryParamState} from '../../common/QueryParamStateHook'
import {SectionHeader} from '../../components/SectionHeader/SectionHeader'
import {useLoggedInUserPermissions, useLoggedInUserProfile} from '../../hooks/useLoggedInUser'
import {useCountriesOrgUnits} from '../../hooks/useOrgUnit'
import {useTranslation} from '../../hooks/useTranslation'
import {Permission} from '../../modules/LoggedInUser'
import {DeleteUserDialog} from '../ManageUser/dialogs/DeleteUserDialog'
import {useDeleteUser} from '../ManageUser/hooks/useDeleteUser'
import {Audience} from '../Messages/Audiences/Audiences'

import {AudienceDetails} from './AudienceDetails'
import {ExportUserDataDialog} from './dialogs/ExportUserDataDialog'
import {useUsers} from './hooks/useUsers'
import {useUsersListFilters} from './hooks/useUsersListFilters'
import {UserDetails} from './UserDetails'
import {useStyles} from './UsersList.styles'
import {UsersTableFilter} from './UsersList.types'
import {UsersListRows} from './UsersListRows'

export const noResultsFromCustomerLookupLabel = 'usersList.lookup.noResultsFromCustomerLookup'
export const noResultsFromPayerLookupLabel = 'usersList.lookup.noResultsFromPayerLookup'
export const tooManySiteIdsLabel = 'usersList.lookup.tooManySiteIds'
export const timeoutCustomersOrPayersLabel = 'usersList.lookup.timeoutCustomersOrPayers'

export type UsersListProps = {
  isCreateAudience?: boolean
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}
// eslint-disable-next-line complexity
export const UsersList = ({
  isCreateAudience,
  loggedInUserPermissions,
  loggedInUserProfile
}: UsersListProps) => {
  const {t} = useTranslation()
  const {classes} = useStyles()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const history = useHistory()
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissionsData)
  const loggedInUserId = loggedInUserProfileData?.user_id
  const [shouldDisableUsersQuery, setShouldDisableUsersQuery] = useState(true)
  const [audienceDetails, setAudienceDetails] = useState(false)
  const {data: features} = useFeaturesCheckFetcher(api)

  const {data: loggedInUserRoles, isLoading: loggedInUserRolesLoading} =
    useUserRoles(loggedInUserId)

  const isInternal =
    loggedInUserRoles && loggedInUserRoles?.length > 0
      ? loggedInUserRoles?.some((item) => item.isInternal)
      : undefined

  const isExternalUser = !!(
    loggedInUserProfileData &&
    !isInternal &&
    !loggedInUserProfileData?.isTester
  )
  const isExternalTestUser = !!(
    loggedInUserProfileData &&
    !isInternal &&
    loggedInUserProfileData?.isTester
  )

  const isCrossBorderApplicable = loggedInUserRoles?.some((userRole) => {
    return (
      userRole.roleType === 'SUPER_ADMIN' &&
      userRole.dataScope &&
      Object.keys(userRole.dataScope).length === 1 &&
      userRole.dataScope.countryId
    )
  })

  const {data: disableCrossBorderUsersFilter} = useDisableCrossCountryFilter(
    loggedInUserRoles,
    isCrossBorderApplicable ?? false
  )

  const shouldShowCrossBorderUsersFilter =
    isCrossBorderApplicable &&
    !disableCrossBorderUsersFilter?.find(
      (feature) => feature.name === 'DisableCrossBorderUsersFilter' && feature?.enabled
    )
  const [areFiltersSetup, setAreFiltersSetup] = useState(false)

  const [filter, setFilter] = useQueryParamState<UsersTableFilter>({
    rowsPerPage: 10,
    page: 1,
    sortColumn: 'createdOn',
    sortDirection: 'desc',
    ...(isInternal !== undefined && (isExternalUser || isExternalTestUser) && {isTester: 'false'})
  })

  const [areFiltersLoading, setAreFiltersLoading] = useState(false)

  const shouldEnableNotifications =
    !!features?.find(
      (feature) =>
        feature.name === 'EnableNotifications' &&
        feature.constraints.every((constraint) => constraint.enabled)
    ) &&
    loggedInUserProfileData?.hasRoles.some((role) =>
      ['EXPORT_ADMIN', 'GLOBAL_ADMIN'].includes(role)
    )

  const shouldEnableSiteIdFilter = loggedInUserProfileData?.hasRoles.every(
    (role) => role !== 'GLOBAL_ADMIN'
  )

  const {
    filterInputValues,
    handleFilterInputValues,
    handleDatePickerFilterInputValues,
    setFilterInputValues,
    setRemovedFilter
  } = useUsersListFilters(
    filter,
    setFilter,
    isExternalUser || isExternalTestUser,
    !!(loggedInUserProfileData && loggedInUserRoles),
    setAreFiltersSetup,
    areFiltersLoading
  )

  const [userDetails, setUserDetails] = useState<User>()

  const {data: userCountries, isLoading: countriesLoading} = useCountriesConfiguration()

  const {roles, isLoading: rolesLoading} = useRolesConfigurationForUserId(loggedInUserProfileData)

  const {orgUnits: orgUnitsOptions, isLoading: isLoadingOrgUnits} = useCountriesOrgUnits(
    userCountries?.map((country) => country.countryCode) || [''],
    !!filterInputValues.find((filter) => filter.filterField === 'orgUnitId')
  )
  const {
    data: response,
    error,
    isInitialLoading: isLoading,
    isFetching
  } = useUsers(filter, !shouldDisableUsersQuery, loggedInUserId)

  useEffect(() => {
    setAudienceDetails(isCreateAudience ? isCreateAudience || isMobile : false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  useEffect(() => {
    if (isInternal !== undefined && shouldDisableUsersQuery) {
      setShouldDisableUsersQuery(false)
    }
  }, [filter])

  useEffect(() => {
    setAreFiltersLoading(filterInputValues.some((value) => value.isLoadingFilterData))
  }, [filterInputValues])

  const [countries, setUserCountries] = useState<Country[]>()
  const {data: viewCrossCountries, isLoading: viewCrossCountriesLoading} =
    useUserCrossCountriesConfiguration(
      loggedInUserRoles,
      isCrossBorderApplicable &&
        filter.loadCrossBorderUsers &&
        filter.loadCrossBorderUsers.toString() === 'true'
    )

  useEffect(() => {
    if (filter.loadCrossBorderUsers) {
      const allCountries = [...(userCountries || []), ...(viewCrossCountries || [])]
      const uniqueCountries = allCountries.reduce((acc, current) => {
        if (!acc.some((country) => country.countryCode === current.countryCode)) {
          acc.push(current)
        }
        return acc
      }, [] as Country[])
      setUserCountries(uniqueCountries)
    } else {
      setUserCountries(userCountries)
    }
  }, [userCountries, filter.loadCrossBorderUsers, viewCrossCountries])

  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)

  const {handleDeleteUser} = useDeleteUser({
    setShowDeleteUserDialog,
    userData: userDetails,
    shouldGoBack: false
  })

  const isCustomerNotFound = error?.message === noResultsFromCustomerLookupLabel
  const isPayerNotFound = error?.message === noResultsFromPayerLookupLabel
  const areTooManySiteIds = error?.message === tooManySiteIdsLabel

  const noResultsMessage = isCustomerNotFound
    ? t(noResultsFromCustomerLookupLabel)
    : isPayerNotFound
      ? t(noResultsFromPayerLookupLabel)
      : areTooManySiteIds
        ? t(tooManySiteIdsLabel)
        : t('usersList.lookup.noResults')

  const setSortKey = (sortKey: string) => {
    const asc = !(filter.sortColumn === sortKey && filter.sortDirection === 'asc')
    setFilter({
      ...filter,
      sortColumn: sortKey,
      sortDirection: asc ? 'asc' : 'desc'
    })
  }

  const totalPageCount = response?.headers ? parseInt(response.headers['x-total-count'], 10) : 0

  const rowsPerPage = Number(filter.rowsPerPage)

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setFilter({...filter, page: newPage})
  }
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({...filter, rowsPerPage: parseInt(event.target.value, 10), page: 1})
  }

  const isCreateNewUserAllowed = loggedInUserPermissionsData.some(
    (permission: Permission) => permission.permissionType === 'ASSIGN_ROLES'
  )

  const handleClose = () => setUserDetails(undefined)

  const handleCloseAudience = (audience?: Audience, discard?: boolean) => {
    if (discard) {
      if (isCreateAudience) {
        history.goBack()
      } else {
        setAudienceDetails(false)
      }
    } else {
      audience
        ? history.push('/messages/create', {
            selectedAudiencesIds: [audience.id],
            redirect: 'users'
          })
        : history.push('/audiences')
    }
  }

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>, userId: string) => {
    event.preventDefault()
    if (userId && userDetails) {
      setShowDeleteUserDialog(true)
    }
  }
  const handleEdit = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    event.preventDefault()
    const basePath = isUserAdminPilot ? `/managePilot/${user.id}` : `/manage/${user.id}`
    const query = filter.loadCrossBorderUsers ? '?includeCrossBorderUser=true' : ''
    const pushLink = `${basePath}${query}`
    history.push(pushLink, {source: 'list', from: window.location.href})
    setUserDetails(undefined)
  }

  const handleDetails = () => {
    if (userDetails) {
      return (
        <UserDetails
          user={userDetails}
          handleClose={handleClose}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          loggedInUserPermissions={loggedInUserPermissionsData}
        />
      )
    }

    if (audienceDetails && !isMobile) {
      return <AudienceDetails filters={formatAudienceFilters()} handleClose={handleCloseAudience} />
    }

    return null
  }

  const mobileAudienceStyle: CSSProperties = {
    background: '#FFFFFF',
    color: '#000000',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: '85vh',
    overflowY: 'scroll',
    zIndex: 1003
  }

  const CloseButton = ({onClose, disabled = false}) => {
    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" height={80}>
        <div>
          <IconButton
            onClick={() => onClose()}
            style={{border: 'none', boxShadow: 'none'}}
            data-test-id="modal-close-button"
            disabled={disabled}
          >
            <Close fontSize="medium" />
          </IconButton>
        </div>
      </Box>
    )
  }

  const formatAudienceFilters = () => {
    return !response?.data || response?.data.length === 0
      ? []
      : filterInputValues
          .filter((inputValue) => !!inputValue.value)
          .map((inputValue) => {
            if (inputValue.filterDropdownOptions && inputValue.filterDropdownOptions?.length > 0) {
              const result = inputValue.filterDropdownOptions?.find(
                (option) => option.value === inputValue.value
              )?.label
              return {
                name: inputValue.filterName || '',
                value: result || inputValue.value || '',
                filterName: inputValue.filterField || '',
                filterValue: inputValue.value || ''
              }
            }
            return {
              name: inputValue.filterName || '',
              value: inputValue.value || '',
              filterName: inputValue.filterField || ''
            }
          })
  }

  const mobileAudienceDetails = (
    <ClickAwayListener
      onClickAway={() => {
        setAudienceDetails(false)
      }}
    >
      <Box style={mobileAudienceStyle}>
        <Box style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <CloseButton onClose={() => setAudienceDetails(false)} />
          <Grid item lg={4} style={{height: '100%'}}>
            <Paper elevation={isMobile ? 0 : 4} style={{height: '100%'}}>
              <Box data-test-id="form-right" style={{padding: '24px 36px', height: '95%'}}>
                <Box data-test-id="form-audience" mb={1} style={{height: '100%'}}>
                  <SectionHeader title={t('usersList.audience.title')} />
                  <AudienceDetails
                    filters={formatAudienceFilters()}
                    handleClose={handleCloseAudience}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Box>
      </Box>
    </ClickAwayListener>
  )

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="userslist-page"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          aside={
            isCreateNewUserAllowed && !isCreateAudience ? (
              <Button
                variant="contained"
                data-test-id="create-user-button"
                onClick={() => history.push('/createUser')}
                className={classes.createButton}
              >
                <Add className={classes.addIcon} />
                {t('usersList.createNewUser')}
              </Button>
            ) : null
          }
        >
          {isCreateAudience ? (
            <span className="mt-3 mb-3">{t('usersList.audience.title')}</span>
          ) : (
            <span>{t('usersList.title')}</span>
          )}
        </PageHeader>
      </div>
      <Grid
        container
        style={{columnGap: '16px', marginTop: '22px', alignItems: 'stretch'}}
        wrap="nowrap"
      >
        <Grid
          item
          md={(userDetails || audienceDetails) && !isMobile ? 8 : 12}
          sm={12}
          xs={12}
          style={{height: '100%'}}
        >
          <Paper elevation={4} style={{padding: isMobile ? '4px 8px' : '26px 36px'}}>
            <ResponsiveTable
              keyField="id"
              columns={UsersListRows({
                countries,
                roles,
                isExternalUser,
                isCreateAudience,
                loggedInUserPermissions: loggedInUserPermissionsData
              })}
              rows={response?.data ?? []}
              onSort={(e, sortKey) => setSortKey(sortKey)}
              sortedBy={filter.sortColumn}
              sortingOrder={filter.sortDirection}
              emptyMessage={!isLoading && noResultsMessage}
              loading={
                isLoading ||
                isFetching ||
                countriesLoading ||
                viewCrossCountriesLoading ||
                rolesLoading ||
                loggedInUserRolesLoading ||
                !areFiltersSetup
              }
              isMobile={isMobile}
              isRowSelectable={isMobile || isCreateAudience ? undefined : () => true}
              {...(!isCreateAudience && {
                onRowClick: (item: User) => {
                  if (item.user_id === userDetails?.user_id) {
                    setUserDetails(undefined)
                  } else {
                    setUserDetails(item)
                  }
                }
              })}
              mobileRowDetails={handleDetails()}
              page={filter.page}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              onChangePage={handleChangePage}
              count={totalPageCount}
              enableHorizontalScroll={true}
              enableColumnSelection={true}
              filterListInputValues={filterInputValues}
              setFilterListInputValues={setFilterInputValues}
              onFilterInputChange={(props: TableFilterItemProps) =>
                props.filterDatePickerRangeOptions
                  ? handleDatePickerFilterInputValues(props)
                  : handleFilterInputValues(props)
              }
              onFilterRemove={(filter: TableFilterType) => {
                setRemovedFilter(filter)
                setFilterInputValues(
                  filterInputValues.filter((inputValue) => {
                    return inputValue.filterType === TableFilterTypeProps.DATEPICKER_RANGE
                      ? inputValue.filterDatePickerRangeOptions?.validTo?.filterField !==
                          filter.filterDatePickerRangeOptions?.validTo?.filterField
                      : inputValue.filterField !== filter.filterField
                  })
                )
              }}
              extraFilters={[
                {
                  filterName: t('usersList.lookup.options.customerId'),
                  filterField: 'customerId',
                  filterType: TableFilterTypeProps.TEXT
                },
                {
                  filterName: t('usersList.lookup.options.payerId'),
                  filterField: 'payerId',
                  filterType: TableFilterTypeProps.TEXT
                },
                {
                  filterName: t('usersList.lookup.options.contractId'),
                  filterField: 'contractId',
                  filterType: TableFilterTypeProps.TEXT
                },
                {
                  filterName: t('usersList.lookup.options.plantId'),
                  filterField: 'plantId',
                  filterType: TableFilterTypeProps.TEXT
                },
                {
                  filterName: t('usersList.lookup.options.businessLine'),
                  filterField: 'businessLine',
                  filterType: TableFilterTypeProps.DROPDOWN,
                  filterDropdownOptions: [
                    {
                      value: 'cem',
                      label: t('usersList.businessLine.cem')
                    },
                    {
                      value: 'agg',
                      label: t('usersList.businessLine.agg')
                    },
                    {
                      value: 'rmc',
                      label: t('usersList.businessLine.rmc')
                    }
                  ]
                },
                {
                  filterName: t('usersList.lookup.options.orgUnitId'),
                  filterField: 'orgUnitId',
                  filterType: TableFilterTypeProps.DROPDOWN,
                  isLoadingFilterData: isLoadingOrgUnits,
                  filterDropdownOptions: orgUnitsOptions.map((option) => ({
                    value: option.key,
                    label: option.label
                  }))
                },
                ...(shouldEnableSiteIdFilter
                  ? [
                      {
                        filterName: t('usersList.lookup.options.siteId'),
                        filterField: 'mhcSiteId',
                        filterType: TableFilterTypeProps.TEXT
                      }
                    ]
                  : []),
                ...(shouldShowCrossBorderUsersFilter
                  ? [
                      {
                        filterName: t('usersList.crossBorderUsers'),
                        filterField: 'loadCrossBorderUsers',
                        filterType: TableFilterTypeProps.DROPDOWN,
                        filterDropdownOptions: [
                          {
                            value: 'true',
                            label: t('usersList.yes')
                          },
                          {
                            value: 'false',
                            label: t('usersList.no')
                          }
                        ]
                      }
                    ]
                  : [])
              ]}
              storeFiltersOptions={
                loggedInUserId
                  ? {
                      userId: loggedInUserId,
                      tableName: isCreateAudience ? 'createAudience' : 'users'
                    }
                  : undefined
              }
              tableHeight="70vh"
              stickyHeader={true}
              enablePinning={true}
              rowDetailsAvailable={!isCreateAudience}
              actionButtons={
                shouldEnableNotifications && (isCreateAudience ? isMobile : false)
                  ? [
                      {
                        name: 'audienceDetails',
                        icon: <Mail style={{color: 'rgb(1, 106, 212)'}} />,
                        onClick: () => {
                          setUserDetails(undefined)
                          setAudienceDetails(true)
                        }
                      }
                    ]
                  : undefined
              }
              {...(loggedInUserProfileData?.hasRoles.includes('EXPORT_ADMIN') &&
                !isCreateAudience &&
                (process.env.REACT_APP_STAGE === 'qa' ||
                  process.env.REACT_APP_STAGE === 'prod') && {
                  onExportData: () => setShowExportDialog(true)
                })}
            />
          </Paper>
        </Grid>
        {!isMobile && (userDetails || audienceDetails) ? (
          <Grid item md={4} sm={0} xs={0}>
            <Paper elevation={4} style={{padding: '26px 32px 26px 32px', height: '100%'}}>
              {handleDetails()}
            </Paper>
          </Grid>
        ) : null}
      </Grid>
      {showDeleteUserDialog && (
        <DeleteUserDialog
          showDeleteUserDialog={showDeleteUserDialog}
          setShowDeleteUserDialog={setShowDeleteUserDialog}
          handleDeleteUser={handleDeleteUser}
        />
      )}
      {showExportDialog && (
        <ExportUserDataDialog
          showExportUserDataDialog={showExportDialog}
          setShowExportUserDataDialog={setShowExportDialog}
          userId={loggedInUserId}
        />
      )}
      {audienceDetails && isMobile && mobileAudienceDetails}
    </Content>
  )
}
